import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ReactLenis } from "lenis/react";
import {
  Plane, Ship, Truck, ArrowRight, Package, Globe, Warehouse, Phone, Mail,
  ShieldCheck, Headphones, CalendarClock, Box, Award, CheckCircle2, MapPin,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1763623252413-579b4695557b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxkZWxpdmVyeSUyMHRydWNrJTIwbW90aW9uJTIwYmx1ciUyMGNpdHl8ZW58MHx8fHwxNzg3OTg2MTc0fDA&ixlib=rb-4.1.0&q=85";
const FTL_IMG = "https://images.unsplash.com/photo-1501700493788-fa1a4fc9fe62?fm=jpg&q=80&w=1200&auto=format&fit=crop";
const WAREHOUSE_IMG = "https://images.unsplash.com/photo-1592085198739-ffcad7f36b54?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxsb2dpc3RpY3MlMjB3YXJlaG91c2UlMjBkZWxpdmVyeSUyMGRyb25lfGVufDB8fHx8MTc4Nzk4NjE3NHww&ixlib=rb-4.1.0&q=85";
const BOXES_IMG = "https://images.unsplash.com/photo-1577705998148-6da4f3963bc8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxlLWNvbW1lcmNlJTIwcGFja2FnaW5nJTIwYm94ZXN8ZW58MHx8fHwxNzg3OTg2MTc0fDA&ixlib=rb-4.1.0&q=85";

const RevealLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const FadeUp = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 32 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay, ease: "easeOut" }}
    className={className}
  >
    {children}
  </motion.div>
);

const Overline = ({ children }) => (
  <p className="text-xs font-bold uppercase tracking-[0.25em] font-mono text-orange-500">{children}</p>
);

const Logo = ({ light = true }) => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
    <img src="/logo.png" alt="ShipZilla logo" className="w-11 h-11 rounded-2xl" />
    <span className="leading-none">
      <span className={`font-display font-black text-xl tracking-tight ${light ? "text-white" : "text-[#0A1628]"}`}>
        SHIP<span className="text-orange-500">ZILLA</span>
      </span>
      <span className={`block text-[9px] font-mono font-bold uppercase tracking-[0.3em] mt-1 ${light ? "text-zinc-400" : "text-zinc-500"}`}>
        The King of Shipping
      </span>
    </span>
  </Link>
);

const Navbar = () => (
  <header data-testid="main-nav" className="sticky top-0 z-50 bg-[#0A1628]/90 backdrop-blur-xl border-b border-white/10">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 h-[72px] flex items-center justify-between">
      <Logo />
      <nav className="hidden lg:flex items-center gap-7 text-[13px] font-bold uppercase tracking-wider text-zinc-300">
        <a href="#hero" data-testid="nav-home-link" className="text-orange-500">Home</a>
        <a href="#about" data-testid="nav-about-link" className="hover:text-orange-500 transition-colors duration-200">About Us</a>
        <a href="#services" data-testid="nav-services-link" className="hover:text-orange-500 transition-colors duration-200">Services</a>
        <a href="#solutions" data-testid="nav-solutions-link" className="hover:text-orange-500 transition-colors duration-200">Solutions</a>
        <a href="#pricing" data-testid="nav-pricing-link" className="hover:text-orange-500 transition-colors duration-200">Pricing</a>
        <a href="#tracking" data-testid="nav-tracking-link" className="hover:text-orange-500 transition-colors duration-200">Tracking</a>
        <a href="#contact" data-testid="nav-contact-link" className="hover:text-orange-500 transition-colors duration-200">Contact Us</a>
      </nav>
      <div className="flex items-center gap-3">
        <Link to="/login" data-testid="nav-login-link"
          className="text-[13px] font-bold uppercase tracking-wider px-5 py-2.5 border border-white/40 text-white hover:border-orange-500 hover:text-orange-500 transition-colors duration-200">
          Login
        </Link>
        <Link to="/signup" data-testid="nav-signup-cta"
          className="text-[13px] font-bold uppercase tracking-wider px-5 py-2.5 bg-orange-500 text-white hover:bg-orange-600 transition-colors duration-200">
          Sign Up
        </Link>
      </div>
    </div>
  </header>
);

const MODES = [
  { icon: Plane, title: "Air Freight", sub: "Fast & Reliable" },
  { icon: Ship, title: "Sea Freight", sub: "Cost-Effective" },
  { icon: Truck, title: "Land Shipping", sub: "Pan India Delivery" },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 100]);

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative bg-[#0A1628] text-white overflow-hidden">
      <motion.img
        src={HERO_IMG}
        alt="ShipZilla freight network"
        style={{ y: imgY, scale: 1.12 }}
        className="absolute inset-0 w-full h-full object-cover opacity-25"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628] via-[#0A1628]/85 to-[#0A1628]/40" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 pt-20 pb-40 lg:pt-28 lg:pb-48">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
              <Overline>One-stop shipping platform · India</Overline>
            </motion.div>
            <h1 className="font-display font-black tracking-tight text-5xl sm:text-7xl lg:text-[86px] leading-[0.98] mt-5">
              <RevealLine delay={0.3}>SHIP ANYTHING,</RevealLine>
              <RevealLine delay={0.45}><span className="text-orange-500">ANYWHERE</span></RevealLine>
            </h1>
            <FadeUp delay={0.75} className="mt-6 max-w-lg">
              <p className="text-base md:text-lg text-zinc-300 leading-relaxed">
                Your one-stop shipping platform for domestic &amp; international deliveries.
              </p>
            </FadeUp>

            <FadeUp delay={0.9} className="mt-10 grid grid-cols-3 gap-3 max-w-xl" data-testid="hero-modes">
              {MODES.map((m) => (
                <div key={m.title} className="bg-white/5 backdrop-blur-md border border-white/15 p-4 sm:p-5 hover:border-orange-500/60 transition-colors duration-300">
                  <m.icon className="w-6 h-6 sm:w-7 sm:h-7 text-orange-500" />
                  <p className="font-bold text-xs sm:text-sm mt-3 uppercase tracking-wide">{m.title}</p>
                  <p className="text-[10px] sm:text-[11px] font-mono text-zinc-400 mt-1 uppercase tracking-wider">{m.sub}</p>
                </div>
              ))}
            </FadeUp>

            <FadeUp delay={1.05} className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/signup" data-testid="hero-start-cta"
                className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold uppercase tracking-wider text-sm px-8 py-4 hover:bg-orange-600 transition-colors duration-200">
                Book Shipment <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/login" data-testid="hero-login-cta"
                className="inline-flex items-center gap-2 font-bold uppercase tracking-wider text-sm px-8 py-4 border border-white/40 text-white hover:border-orange-500 hover:text-orange-500 transition-colors duration-200">
                <MapPin className="w-4 h-4" /> Track Shipment
              </Link>
            </FadeUp>
          </div>

          <div className="lg:col-span-4 flex lg:justify-end">
            <FadeUp delay={1.15} className="lg:border-l-2 lg:border-orange-500 lg:pl-6 self-start">
              <p className="text-xs font-mono font-bold uppercase tracking-[0.25em] text-zinc-400">Delivering to</p>
              <p className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-2">
                220+ <span className="text-orange-500">COUNTRIES</span>
              </p>
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-zinc-400 mt-2">Across the globe</p>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
};

const FEATURES = [
  { icon: Globe, title: "220+ Countries", desc: "Global reach, local expertise" },
  { icon: Box, title: "B2C Shipping", desc: "Reliable delivery for your customers" },
  { icon: Warehouse, title: "B2B Shipping", desc: "Efficient logistics for your business" },
  { icon: CalendarClock, title: "Real Time Tracking", desc: "Track your shipment anytime, anywhere" },
  { icon: ShieldCheck, title: "Secure & Trusted", desc: "Safe packaging, on-time delivery" },
  { icon: Headphones, title: "24/7 Customer Support", desc: "We're here to help you, always" },
];

const FeatureCards = () => (
  <section data-testid="feature-cards" className="relative bg-[#0A1628] pb-16">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 -mt-24 relative z-10">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {FEATURES.map((f, i) => (
          <FadeUp key={f.title} delay={i * 0.06}
            className="bg-white/5 backdrop-blur-md border border-white/15 p-5 hover:border-orange-500/60 hover:bg-white/10 transition-all duration-300">
            <f.icon className="w-6 h-6 text-orange-500" />
            <p className="font-bold text-sm mt-3 text-white uppercase tracking-wide leading-snug">{f.title}</p>
            <p className="text-[11px] text-zinc-400 mt-1.5 leading-relaxed">{f.desc}</p>
          </FadeUp>
        ))}
      </div>
      <FadeUp delay={0.2} className="mt-6 bg-white/5 backdrop-blur-md border border-white/15 px-6 py-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6" data-testid="stats-bar">
        {[
          { icon: Package, v: "10mn+", l: "B2C Shipments" },
          { icon: Box, v: "2mn+", l: "B2B Shipments" },
          { icon: Truck, v: "22+", l: "Courier Partners" },
          { icon: MapPin, v: "22000+", l: "Pincode Availability" },
          { icon: Globe, v: "220+", l: "Countries" },
          { icon: Award, v: "100%", l: "Customer Satisfaction" },
        ].map((s) => (
          <div key={s.l} className="flex items-center gap-4">
            <s.icon className="w-7 h-7 text-orange-500 shrink-0" />
            <div>
              <p className="font-display font-black text-2xl tracking-tight text-orange-500">{s.v}</p>
              <p className="text-xs text-zinc-400 mt-0.5">{s.l}</p>
            </div>
          </div>
        ))}
      </FadeUp>
    </div>
  </section>
);

const SERVICES = [
  {
    title: "B2C Logistics", img: BOXES_IMG, icon: Package,
    desc: "eCommerce parcel delivery from ₹20/500g with smart courier allocation, branded tracking and COD remittance in 2 days.",
    points: ["Same-day dispatch", "NDR autopilot", "Branded tracking pages"],
  },
  {
    title: "B2B Logistics", img: FTL_IMG, icon: Truck,
    desc: "Full truckload (FTL) consignments and part-load freight across surface and cargo networks — transparent quotes, verified drivers, live GPS.",
    points: ["Trucks in ~30 minutes", "Freight from ₹6/kg", "POD & documentation"],
  },
  {
    title: "International Logistics", img: WAREHOUSE_IMG, icon: Globe,
    desc: "Cross-border shipping to 220+ countries and territories with customs, duties and documentation handled end to end.",
    points: ["ShipX global suite", "Customs handled", "B2B cargo (CargoX)"],
  },
  {
    title: "3PL & Fulfillment", img: WAREHOUSE_IMG, icon: Warehouse,
    desc: "35 last-mile-enabled warehouses. Store closer to buyers, cut shipping cost by 20% and slash RTO by up to 60%.",
    points: ["35 warehouses", "99.9% pick accuracy", "Same-day fulfillment"],
  },
];

const Services = () => (
  <section id="services" data-testid="services-section" className="bg-[#0D1B2E] py-24 border-t border-white/5">
    <div className="max-w-7xl mx-auto px-4 sm:px-8">
      <FadeUp>
        <Overline>Our Services</Overline>
        <div className="flex flex-wrap items-end justify-between gap-6 mt-4">
          <h2 className="font-display font-black tracking-tight text-4xl sm:text-5xl text-white max-w-2xl">
            Logistics for every size of business
          </h2>
          <p className="text-sm text-zinc-400 max-w-sm">
            From a single parcel to full truckloads and international freight — one partner, one platform.
          </p>
        </div>
      </FadeUp>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
        {SERVICES.map((s, i) => (
          <FadeUp key={s.title} delay={i * 0.08}
            className="group bg-white/5 backdrop-blur-md border border-white/10 flex flex-col hover:border-orange-500/60 transition-colors duration-300">
            <div className="h-44 overflow-hidden">
              <img src={s.img} alt={s.title} className="w-full h-full object-cover opacity-80 group-hover:scale-105 group-hover:opacity-100 transition-all duration-500" />
            </div>
            <div className="p-6 flex-1 flex flex-col">
              <s.icon className="w-6 h-6 text-orange-500" />
              <h3 className="font-display font-extrabold text-xl mt-3 tracking-tight text-white">{s.title}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed mt-2 flex-1">{s.desc}</p>
              <ul className="mt-4 space-y-1.5">
                {s.points.map((p) => (
                  <li key={p} className="text-xs font-semibold text-zinc-400 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-orange-500" /> {p}
                  </li>
                ))}
              </ul>
              <Link to="/signup" className="mt-5 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-orange-500 group-hover:gap-3 transition-all duration-200">
                Know more <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const About = () => (
  <section id="about" data-testid="about-section" className="bg-[#0A1628] py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
      <FadeUp>
        <Overline>About Us</Overline>
        <h2 className="font-display font-black tracking-tight text-4xl sm:text-5xl text-white mt-4 leading-[1.02]">
          The King of Shipping — <span className="text-orange-500">built in India</span>
        </h2>
        <p className="text-zinc-400 mt-6 leading-relaxed max-w-lg">
          ShipZilla started with one belief: logistics shouldn't need follow-ups. Today we run
          B2C parcels, B2B freight, air express and international lanes for thousands of sellers —
          on one dashboard, one wallet and one support team that actually answers.
        </p>
        <ul className="mt-8 space-y-3">
          {[
            "19,000+ Indian pin codes, metros to the last village",
            "42+ courier partners on one rate card",
            "COD remittance in 2 days, wallet-first billing",
            "KYC-verified merchants and insured freight",
          ].map((p) => (
            <li key={p} className="flex items-start gap-3 text-sm text-zinc-300">
              <CheckCircle2 className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" /> {p}
            </li>
          ))}
        </ul>
      </FadeUp>
      <FadeUp delay={0.15} className="relative">
        <div className="border border-white/15 overflow-hidden">
          <img src={WAREHOUSE_IMG} alt="ShipZilla fulfillment centre" className="w-full h-[420px] object-cover opacity-90" />
        </div>
        <div className="absolute -bottom-6 -left-6 bg-orange-500 text-white px-7 py-5">
          <p className="font-display font-black text-3xl tracking-tight">5L+</p>
          <p className="text-xs font-mono uppercase tracking-widest mt-1">Shipments every day</p>
        </div>
      </FadeUp>
    </div>
  </section>
);

const SECTORS = ["eCommerce & D2C", "Apparel & Fashion", "Pharma & Healthcare", "Electronics", "FMCG & Grocery", "Automotive Parts", "Furniture & Home", "Books & Stationery"];

const Solutions = () => (
  <section id="solutions" data-testid="solutions-section" className="bg-[#0D1B2E] py-24 border-t border-white/5">
    <div className="max-w-7xl mx-auto px-4 sm:px-8">
      <FadeUp>
        <Overline>Solutions</Overline>
        <h2 className="font-display font-black tracking-tight text-4xl sm:text-5xl text-white mt-4">
          Industries we move
        </h2>
      </FadeUp>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
        {SECTORS.map((s, i) => (
          <FadeUp key={s} delay={i * 0.04}
            className="bg-white/5 backdrop-blur-md border border-white/10 px-5 py-6 text-sm font-bold text-zinc-200 hover:border-orange-500/60 hover:text-orange-500 transition-colors duration-200 cursor-default text-center">
            {s}
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const PLANS = [
  {
    name: "LAUNCH", price: "₹0", period: "forever", popular: false,
    features: ["1 channel integration", "Up to 50 shipments / mo", "Standard courier rates", "Email support"],
  },
  {
    name: "GROWTH", price: "₹999", period: "/month", popular: true,
    features: ["Unlimited integrations", "Up to 1,000 shipments / mo", "Lowest negotiated rates", "NDR autopilot + RTO suite", "Priority support"],
  },
  {
    name: "SCALE", price: "₹2,499", period: "/month", popular: false,
    features: ["Everything in Growth", "Unlimited shipments", "Dedicated account manager", "Fulfillment access", "ShipX cross-border suite"],
  },
];

const Pricing = () => (
  <section id="pricing" data-testid="pricing-section" className="bg-[#0A1628] py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-8">
      <FadeUp>
        <Overline>Pricing</Overline>
        <h2 className="font-display font-black tracking-tight text-4xl sm:text-5xl text-white mt-4">
          Simple plans, royal treatment
        </h2>
      </FadeUp>
      <div className="grid md:grid-cols-3 gap-5 mt-14">
        {PLANS.map((p, i) => (
          <FadeUp key={p.name} delay={i * 0.08}
            className={`flex flex-col p-8 border backdrop-blur-md ${p.popular ? "bg-orange-500 border-orange-500 text-white" : "bg-white/5 border-white/10 text-white hover:border-orange-500/50"} transition-colors duration-300`}>
            {p.popular && (
              <span className="self-start bg-white text-orange-600 text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-1 mb-4">Most Popular</span>
            )}
            <h3 className="font-mono font-bold tracking-[0.25em] text-xs uppercase">{p.name}</h3>
            <div className="mt-4 flex items-end gap-1">
              <span className="font-display font-black text-5xl tracking-tight">{p.price}</span>
              <span className={`text-sm mb-1.5 ${p.popular ? "text-orange-100" : "text-zinc-400"}`}>{p.period}</span>
            </div>
            <ul className="mt-8 space-y-3 flex-1">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${p.popular ? "text-white" : "text-orange-500"}`} />
                  <span className={p.popular ? "text-orange-50" : "text-zinc-300"}>{f}</span>
                </li>
              ))}
            </ul>
            <Link to="/signup" data-testid={`pricing-cta-${p.name.toLowerCase()}`}
              className={`mt-10 text-center font-bold uppercase tracking-wider text-sm px-6 py-3.5 transition-colors duration-200 ${
                p.popular ? "bg-[#0A1628] text-white hover:bg-black" : "bg-orange-500 text-white hover:bg-orange-600"
              }`}>
              Start with {p.name}
            </Link>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const TESTIMONIALS = [
  {
    quote: "I started my skincare brand from my hostel room. ShipZilla's pickups were so reliable that my customers thought I had a full warehouse team. RTO dropped to nearly zero.",
    name: "Anaya Kapoor", role: "Founder, BlushKart",
  },
  {
    quote: "Being from a tier-2 city, on-time pan-India delivery felt impossible. With ShipZilla's fulfillment centres we now deliver 2 days faster and shipping cost is down 12%.",
    name: "Santosh Verma", role: "Ops Head, PhoolMali",
  },
  {
    quote: "ShipX took our attar brand to the US and UK without a single customs headache. Labels, tracking, COD reconciliation — everything sits in one clean dashboard.",
    name: "Adil Sheikh", role: "Founder, AQ Perfumes",
  },
];

const Testimonials = () => (
  <section data-testid="testimonials-section" className="bg-[#0D1B2E] py-24 border-t border-white/5">
    <div className="max-w-7xl mx-auto px-4 sm:px-8">
      <FadeUp>
        <Overline>Customer Stories</Overline>
        <h2 className="font-display font-black tracking-tight text-4xl sm:text-5xl text-white mt-4">
          Trusted by growing businesses
        </h2>
      </FadeUp>
      <div className="grid md:grid-cols-3 gap-5 mt-14">
        {TESTIMONIALS.map((t, i) => (
          <FadeUp key={t.name} delay={i * 0.08} className="bg-white/5 backdrop-blur-md border border-white/10 p-8 flex flex-col hover:border-orange-500/60 transition-colors duration-300">
            <span className="font-display font-black text-6xl text-orange-500 leading-none">”</span>
            <p className="text-sm leading-relaxed text-zinc-300 mt-4 flex-1">{t.quote}</p>
            <div className="mt-8 pt-6 border-t border-white/10">
              <p className="font-display font-extrabold tracking-tight text-white">{t.name}</p>
              <p className="text-xs font-mono text-zinc-500 mt-1 uppercase tracking-wider">{t.role}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const TrackingBand = () => (
  <section id="tracking" data-testid="tracking-section" className="bg-orange-500 py-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
      <FadeUp>
        <h2 className="font-display font-black tracking-tight text-4xl sm:text-5xl text-white max-w-2xl">
          Track any shipment in real time
        </h2>
        <p className="text-orange-100 font-semibold mt-3">Live milestones, courier events and delivery proof — inside your dashboard.</p>
      </FadeUp>
      <FadeUp delay={0.15} className="flex flex-wrap gap-4">
        <Link to="/login" data-testid="tracking-login-cta"
          className="inline-flex items-center gap-2 bg-[#0A1628] text-white font-bold uppercase tracking-wider text-sm px-8 py-4 hover:bg-black transition-colors duration-200">
          Login &amp; Track <ArrowRight className="w-4 h-4" />
        </Link>
        <Link to="/signup" data-testid="tracking-signup-cta"
          className="inline-flex items-center gap-2 border-2 border-white text-white font-bold uppercase tracking-wider text-sm px-8 py-4 hover:bg-white hover:text-orange-600 transition-colors duration-200">
          Ship With Us
        </Link>
      </FadeUp>
    </div>
  </section>
);

const Footer = () => (
  <footer id="contact" data-testid="footer" className="bg-[#0A1628] text-white pt-16 overflow-hidden border-t border-white/5">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 grid md:grid-cols-4 gap-12 pb-16">
      <div>
        <Logo />
        <p className="text-sm text-zinc-400 mt-5 leading-relaxed max-w-xs">
          The king of shipping. B2C, B2B, air express and international logistics for thousands of Indian sellers.
        </p>
        <ul className="mt-5 space-y-3 text-sm text-zinc-300">
          <li className="flex items-center gap-2.5"><Mail className="w-4 h-4 text-orange-500" /> admin@shipzilla.in</li>
        </ul>
      </div>
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-orange-500 font-bold">Company</p>
        <ul className="mt-5 space-y-2.5 text-sm text-zinc-400">
          {["About Us", "Careers", "Media / Press", "Contact Us", "ESG Commitment"].map((l) => (
            <li key={l}><a href="#about" className="hover:text-orange-500 transition-colors duration-150">{l}</a></li>
          ))}
        </ul>
      </div>
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-orange-500 font-bold">Services</p>
        <ul className="mt-5 space-y-2.5 text-sm text-zinc-400">
          {["B2C Logistics", "B2B Logistics", "International Logistics", "3PL & Fulfillment", "Truck Booking"].map((l) => (
            <li key={l}><a href="#services" className="hover:text-orange-500 transition-colors duration-150">{l}</a></li>
          ))}
        </ul>
      </div>
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-orange-500 font-bold">Legal</p>
        <ul className="mt-5 space-y-2.5 text-sm text-zinc-400">
          {["Privacy Policy", "Vendor Policies", "Terms of Use", "Responsible Disclosure"].map((l) => (
            <li key={l}><a href="#contact" className="hover:text-orange-500 transition-colors duration-150">{l}</a></li>
          ))}
        </ul>
      </div>
    </div>
    <div className="border-t border-white/10">
      <p
        aria-hidden="true"
        className="font-display font-black text-center leading-none select-none text-[18vw] tracking-tight text-transparent"
        style={{ WebkitTextStroke: "1px rgba(249,115,22,0.35)" }}
      >
        SHIPZILLA
      </p>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 pb-8 flex flex-col sm:flex-row justify-between gap-2 text-xs font-mono text-zinc-500">
        <span>© 2026 ShipZilla Logistics Pvt. Ltd. All Rights Reserved.</span>
        <span>Made in India · Ships everywhere</span>
      </div>
    </div>
  </footer>
);

export default function Landing() {
  return (
    <ReactLenis root options={{ anchors: true, lerp: 0.09 }}>
      <div data-testid="landing-page" className="bg-[#0A1628] text-white">
        <Navbar />
        <Hero />
        <FeatureCards />
        <Services />
        <About />
        <Solutions />
        <Pricing />
        <Testimonials />
        <TrackingBand />
        <Footer />
      </div>
    </ReactLenis>
  );
}
